import React, { FC } from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  PaymentInstallmentItem,
  PaymentPlan,
} from "@app/postBill/models/apiControllerModel";
import { convertBalance } from "@app/postBill/utils/convertBalance";
import { getOrdinalSuffix } from "@app/postBill/utils";

import "./paymentSummaryCard.scss";

const messages = defineMessages({
  paymentSummaryCardTestNamesLabel: {
    id: "paymentSummaryCardTestNamesLabel",
    defaultMessage: "Test names",
  },
  paymentSummaryCardDateLabel: {
    id: "paymentSummaryCardDateLabel",
    defaultMessage: "Payment date",
  },
  paymentSummaryCardPaymentLabel: {
    id: "paymentSummaryCardPaymentLabel",
    defaultMessage: "{startIndex} - {endIndex} payments",
  },
  paymentSummaryCardSinglePaymentLabel: {
    id: "paymentSummaryCardSinglePaymentLabel",
    defaultMessage: "{startIndex} payment",
  },
  paymentSummaryCardMonthlyPaymentsLabel: {
    id: "paymentSummaryCardMonthlyPaymentsLabel",
    defaultMessage: "Monthly payments",
  },
  paymentSummaryCardTotalTestsLabel: {
    id: "paymentSummaryCardTotalTestsLabel",
    defaultMessage: "Total ({count, plural, one {# test} other {# tests}})",
  },
});

interface PaymentSummaryCardProps {
  selectedPlan: PaymentPlan;
  testNames: string[];
  testPrices: number[];
  firstPaymentDate: string;
  totalPrice: number;
  currency: string;
  paymentItems?: PaymentInstallmentItem[];
}

const PaymentSummaryCard: FC<PaymentSummaryCardProps> = ({
  selectedPlan = PaymentPlan.None,
  testNames,
  testPrices,
  firstPaymentDate,
  totalPrice,
  currency,
  paymentItems = [],
}) => {
  const intl = useIntl();
  const isInstallment = selectedPlan !== PaymentPlan.None;

  const renderOneTimePayment = () => (
    <div className="payment-summary-card__tests">
      {testNames.map((name, index) => (
        <div key={index} className="payment-summary-card__row">
          <span>{name}</span>
          <span className="payment-summary-card__value">
            {convertBalance(testPrices[index], true, currency)}
          </span>
        </div>
      ))}

      <div className="payment-summary-card__row payment-summary-card__total">
        <span>
          {intl.formatMessage(messages.paymentSummaryCardTotalTestsLabel, {
            count: testNames.length,
          })}
        </span>
        <span className="payment-summary-card__value">
          {convertBalance(totalPrice, true, currency)}
        </span>
      </div>
    </div>
  );

  const renderInstallmentPayment = () => {
    return (
      <>
        <div className="payment-summary-card__row">
          <span>
            {intl.formatMessage(messages.paymentSummaryCardTestNamesLabel)}
          </span>
          <span className="payment-summary-card__value">
            {testNames.join(", ")}
          </span>
        </div>

        <div className="payment-summary-card__row">
          <span>
            {intl.formatMessage(messages.paymentSummaryCardDateLabel)}
          </span>
          <span className="payment-summary-card__value">
            {firstPaymentDate}
          </span>
        </div>

        {paymentItems.map((item, index) => {
          const getPaymentLabel = () => {
            if (paymentItems.length === 1) {
              return intl.formatMessage(
                messages.paymentSummaryCardMonthlyPaymentsLabel
              );
            }

            const hasEndIndex = item.indexes.length > 1;
            const startIndex = getOrdinalSuffix(item.indexes[0], intl);

            if (hasEndIndex) {
              const endIndex = getOrdinalSuffix(item.indexes[1], intl);
              return intl.formatMessage(
                messages.paymentSummaryCardPaymentLabel,
                {
                  startIndex,
                  endIndex,
                }
              );
            }

            return intl.formatMessage(
              messages.paymentSummaryCardSinglePaymentLabel,
              {
                startIndex,
              }
            );
          };

          return (
            <div key={index} className="payment-summary-card__row">
              <span>{getPaymentLabel()}</span>
              <span className="payment-summary-card__value">
                {convertBalance(item.amount, true, currency)}
              </span>
            </div>
          );
        })}

        <div className="payment-summary-card__row payment-summary-card__total">
          <span>
            {intl.formatMessage(messages.paymentSummaryCardTotalTestsLabel, {
              count: testNames.length,
            })}
          </span>
          <span className="payment-summary-card__value">
            {convertBalance(totalPrice, true, currency)}
          </span>
        </div>
      </>
    );
  };

  return (
    <div className="payment-summary-card">
      {isInstallment ? renderInstallmentPayment() : renderOneTimePayment()}
    </div>
  );
};

export default PaymentSummaryCard;
